import React, { useState, useEffect } from "react";
import { AlertTriangle, Shield, CheckCircle, Clock, Circle } from "lucide-react";
import { useTranslation } from "react-i18next";
import "../frameWork/AssessmentScore.css";
import { getComplianceDashboard, getComplianceForEachStandardDashboard, getUsersTasksDashboard } from "../../services/dashboards";
import BarChartComponent from '../../components/charts/charts/BarChart';
import LineChartComponent from '../../components/charts/charts/LineChart';
import PieChart from '../../components/charts/charts/PieChart';

// Type definitions for chart data
interface ChartDataItem {
  label: string;
  value: number;
  color?: string;
}

interface DonutChartProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
}

interface BarChartProps {
  data: ChartDataItem[];
  height?: number;
}

interface LineChartProps {
  data: ChartDataItem[];
  height?: number;
}

interface RadarChartProps {
  data: ChartDataItem[];
  size?: number;
}

// Beautiful chart components using SVG
const DonutChart: React.FC<DonutChartProps> = ({ percentage, size = 120, strokeWidth = 12, color = "hsl(159, 100%, 51%)" }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDasharray = `${(percentage / 100) * circumference} ${circumference}`;
  
  return (
    <div className="donut-chart">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={strokeDasharray}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dasharray 1s ease-in-out' }}
        />
      </svg>
      <div className="donut-label">
        <span className="percentage">{percentage}%</span>
        <span className="label">Score</span>
      </div>
    </div>
  );
};

const BarChart: React.FC<BarChartProps> = ({ data, height = 200 }) => {
  const maxValue = Math.max(...data.map((item: ChartDataItem) => item.value), 100);
  const minValue = 0;
  const step = Math.ceil(maxValue / 5);
  const yAxisLabels = [];
  for (let i = 0; i <= 5; i++) {
    yAxisLabels.push(minValue + step * i);
  }
  const reversedLabels = [...yAxisLabels].reverse();
  
  return (
    <div className="bar-chart-enhanced" style={{ height: height + 60 }}>
      {/* Y-axis labels */}
      <div className="y-axis-labels">
        {reversedLabels.map((label, idx) => (
          <span key={idx} className="y-axis-label">{label}%</span>
        ))}
      </div>
      
      {/* Chart area */}
      <div className="chart-area">
        {/* Grid lines */}
        <div className="grid-lines">
          {yAxisLabels.map((_, idx) => (
            <div key={idx} className="grid-line" />
          ))}
        </div>
        
        {/* Bars */}
        <div className="bars-enhanced">
          {data.map((item: ChartDataItem, index: number) => {
            const barHeight = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
            const color = item.color || 'hsl(159, 100%, 51%)';
            return (
              <div key={index} className="bar-group-enhanced">
                <div className="bar-wrapper">
                  <div 
                    className="bar-enhanced" 
                    style={{ 
                      height: `${barHeight}%`,
                      background: `linear-gradient(180deg, ${color} 0%, ${color}dd 100%)`,
                      boxShadow: `0 4px 12px ${color}40`
                    }}
                    title={`${item.label}: ${item.value}%`}
                  >
                    <span className="bar-value-enhanced">{item.value}%</span>
                  </div>
                </div>
                <span className="bar-label-enhanced">{item.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const LineChart: React.FC<LineChartProps> = ({ data, height = 200 }) => {
  const maxValue = Math.max(...data.map((item: ChartDataItem) => item.value), 100);
  const minValue = Math.min(...data.map((item: ChartDataItem) => item.value), 0);
  const range = maxValue - minValue || 100;
  const step = Math.ceil((maxValue - minValue) / 5) || 20;
  const yAxisLabels = [];
  for (let i = 0; i <= 5; i++) {
    yAxisLabels.push(Math.round(minValue + step * i));
  }
  const reversedLabels = [...yAxisLabels].reverse();
  
  // Add padding to SVG viewBox for better spacing
  const padding = 5;
  const chartWidth = 100 - padding * 2;
  const chartHeight = 100 - padding * 2;
  
  const points = data.map((item: ChartDataItem, index: number) => {
    const x = data.length > 1 
      ? padding + (index / (data.length - 1)) * chartWidth 
      : padding + chartWidth / 2;
    const y = padding + chartHeight - ((item.value - minValue) / range) * chartHeight;
    return `${x},${y}`;
  }).join(' ');
  
  // Create area path (line + bottom closure)
  const areaPath = points 
    ? `M${points} L${padding + chartWidth},${padding + chartHeight} L${padding},${padding + chartHeight} Z`
    : '';
  
  return (
    <div className="line-chart-enhanced" style={{ height: height + 80 }}>
      {/* Y-axis labels */}
      <div className="y-axis-labels-line">
        {reversedLabels.map((label, idx) => (
          <span key={idx} className="y-axis-label-line">{label}%</span>
        ))}
      </div>
      
      {/* Chart area */}
      <div className="chart-area-line">
        {/* Grid lines */}
        <div className="grid-lines-line">
          {yAxisLabels.map((_, idx) => (
            <div key={idx} className="grid-line-line" />
          ))}
        </div>
        
        {/* SVG Chart */}
        <svg 
          width="100%" 
          height="100%" 
          viewBox="0 0 100 100" 
          preserveAspectRatio="none" 
          className="line-chart-svg"
        >
          {/* Gradient definition */}
          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="hsl(159, 100%, 51%)" stopOpacity="0.3" />
              <stop offset="50%" stopColor="hsl(159, 100%, 51%)" stopOpacity="0.15" />
              <stop offset="100%" stopColor="hsl(159, 100%, 51%)" stopOpacity="0.02" />
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          
          {/* Area fill */}
          {areaPath && (
            <path
              d={areaPath}
              fill="url(#lineGradient)"
              style={{ transition: 'all 1s ease-in-out' }}
            />
          )}
          
          {/* Line */}
          <polyline
            fill="none"
            stroke="hsl(159, 100%, 51%)"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            points={points}
            style={{ transition: 'all 1s ease-in-out' }}
            className="line-chart-polyline"
            filter="url(#glow)"
          />
          
          {/* Data points */}
          {data.map((item: ChartDataItem, index: number) => {
            const x = data.length > 1 
              ? padding + (index / (data.length - 1)) * chartWidth 
              : padding + chartWidth / 2;
            const y = padding + chartHeight - ((item.value - minValue) / range) * chartHeight;
            return (
              <g key={index} className="line-point-group">
                {/* Halo effect */}
                <circle
                  cx={x}
                  cy={y}
                  r="8"
                  fill="hsl(159, 100%, 51%)"
                  opacity="0.15"
                  className="line-point-halo"
                  style={{ transition: 'all 0.3s ease-in-out' }}
                />
                {/* Main point */}
                <circle
                  cx={x}
                  cy={y}
                  r="5"
                  fill="hsl(159, 100%, 51%)"
                  stroke="#ffffff"
                  strokeWidth="3"
                  className="line-point"
                  style={{ transition: 'all 0.3s ease-in-out' }}
                />
                {/* Inner dot */}
                <circle
                  cx={x}
                  cy={y}
                  r="2"
                  fill="#ffffff"
                  className="line-point-inner"
                />
                {/* Value label */}
                <text
                  x={x}
                  y={y - 12}
                  textAnchor="middle"
                  className="line-point-value"
                  fill="#1e293b"
                  fontSize="2.8"
                  fontWeight="700"
                  opacity="0"
                  style={{ transition: 'opacity 0.3s ease-in-out' }}
                >
                  {item.value}%
                </text>
              </g>
            );
          })}
        </svg>
        
        {/* X-axis labels */}
        <div className="line-labels-enhanced">
          {data.map((item: ChartDataItem, index: number) => (
            <span 
              key={index} 
              className="line-label-enhanced" 
              title={`${item.label}: ${item.value}%`}
            >
              {item.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const RadarChart: React.FC<RadarChartProps> = ({ data, size = 200 }) => {
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = size / 3;
  
  const points = data.map((item: ChartDataItem, index: number) => {
    const angle = (index / data.length) * 2 * Math.PI - Math.PI / 2;
    const value = item.value / 100;
    const x = centerX + Math.cos(angle) * radius * value;
    const y = centerY + Math.sin(angle) * radius * value;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <div className="radar-chart">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background circles */}
        <circle cx={centerX} cy={centerY} r={radius} fill="none" stroke="#e2e8f0" strokeWidth="1" />
        <circle cx={centerX} cy={centerY} r={radius * 0.75} fill="none" stroke="#e2e8f0" strokeWidth="1" />
        <circle cx={centerX} cy={centerY} r={radius * 0.5} fill="none" stroke="#e2e8f0" strokeWidth="1" />
        <circle cx={centerX} cy={centerY} r={radius * 0.25} fill="none" stroke="#e2e8f0" strokeWidth="1" />
        
        {/* Radar area */}
        <polygon
          points={points}
          fill="hsla(159, 100%, 51%, 0.2)"
          stroke="hsl(159, 100%, 51%)"
          strokeWidth="2"
        />
        
        {/* Data points */}
        {data.map((item: ChartDataItem, index: number) => {
          const angle = (index / data.length) * 2 * Math.PI - Math.PI / 2;
          const value = item.value / 100;
          const x = centerX + Math.cos(angle) * radius * value;
          const y = centerY + Math.sin(angle) * radius * value;
          return (
            <circle
              key={index}
              cx={x}
              cy={y}
              r="4"
              fill="hsl(159, 100%, 51%)"
            />
          );
        })}
      </svg>
      <div className="radar-labels">
        {data.map((item: ChartDataItem, index: number) => (
          <span key={index} className="radar-label">
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
};

const ResultAssessment = () => {
  const { t } = useTranslation();
  // State for dashboard data
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [complianceStatus, setComplianceStatus] = useState<any>([]);
  const [monthlyTasks, setMonthlyTasks] = useState<any[]>([]);
  const [complianceForEachStandard, setComplianceForEachStandard] = useState<any[]>([]);
  const [usersTasksDashboard, setUsersTasksDashboard] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch dashboard data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch compliance dashboard
        const complianceData = await getComplianceDashboard();
        if (complianceData.success) {
          setDashboardData(complianceData.data);
          // Align compliance status structure with ntra-regtech / Dashboard.tsx
          setComplianceStatus([{
            'Compliance': complianceData.data?.compliance,
            'Non_Compliance': complianceData.data?.non_compliance,
            'Partially': complianceData.data?.partially,
            'Not_Started': complianceData.data?.not_started,
            'In_Progress': complianceData.data?.in_progress,
            'Done': complianceData.data?.done,
            'Overdue': complianceData.data?.overdue,
          }]);
          
          // Format monthly tasks like Dashboard.tsx
          const monthlyTasksWithMonthNames = complianceData.data.monthlyTasks
            ? complianceData.data.monthlyTasks.map((task: any, index: number) => {
                const monthNames = [t("Jan"), t("Feb"), t("Mar"), t("Apr"), t("May"), t("Jun"), t("Jul"), t("Aug"), t("Sep"), t("Oct"), t("Nov"), t("Dec")];
                return { name: monthNames[index], noTasks: task };
              })
            : [];
          setMonthlyTasks(monthlyTasksWithMonthNames);
        }

        // Fetch compliance for each standard
        const standardsData = await getComplianceForEachStandardDashboard();
        if (standardsData.success) {
          // Align standards compliance structure with ntra-regtech / Dashboard.tsx
          const formattedData = standardsData.data
            ? Object.keys(standardsData.data).map((key: string) => ({
                id: key,
                reference_name: standardsData.data[key].reference_name,
                fully_implemented: standardsData.data[key].fully_implemented,
                mostly_implemented: standardsData.data[key].mostly_implemented,
                averagely_implemented: standardsData.data[key].averagely_implemented,
                implemented: standardsData.data[key].implemented,
                partially_compliant: standardsData.data[key].partially_compliant,
                non_compliant: standardsData.data[key].non_compliant,
              }))
            : [];
          setComplianceForEachStandard(formattedData);
        }

        // Fetch users tasks
        const tasksData = await getUsersTasksDashboard();
        if (tasksData.success) {
          setUsersTasksDashboard(tasksData.data || []);
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Static assessment data
  const overallScore = 78;
  const objectiveScores = [
    { label: 'Risk Management', value: 85, color: 'hsl(159, 100%, 51%)' },
    { label: 'Compliance Framework', value: 72, color: '#10b981' },
    { label: 'Control Implementation', value: 68, color: '#3b82f6' },
    { label: 'Monitoring & Reporting', value: 91, color: '#8b5cf6' },
    { label: 'Incident Response', value: 76, color: '#ef4444' }
  ];

  const controlMappingData = [
    { label: 'ISO 27001', value: 82 },
    { label: 'NIST CSF', value: 75 },
    { label: 'GDPR', value: 88 },
    { label: 'SOC 2', value: 69 },
    { label: 'PCI DSS', value: 73 }
  ];

  const standardMappingData = [
    { label: 'Security Controls', value: 85 },
    { label: 'Access Management', value: 78 },
    { label: 'Data Protection', value: 92 },
    { label: 'Incident Management', value: 71 },
    { label: 'Business Continuity', value: 84 },
    { label: 'Risk Assessment', value: 79 }
  ];

  const timelineData = [
    { label: 'Q1', value: 45 },
    { label: 'Q2', value: 58 },
    { label: 'Q3', value: 72 },
    { label: 'Q4', value: 78 }
  ];

  // Transform dashboard data for charts
  const standardsComplianceData = complianceForEachStandard.map((standard: any) => ({
    label: standard.reference_name || standard.id,
    value: standard.compliant || 0
  }));

  // Transform monthlyTasks to LineChart format (from { name, noTasks } to { label, value })
  const simpleTasksLineData = monthlyTasks.map((task: any) => ({
    label: task.name || '',
    value: task.noTasks || 0
  }));

  // Transform complianceStatus to BarChart format (from [{ 'Compliance': x, 'Partial': y, ... }] to [{ label, value, color }])
  const complianceStatusData = complianceStatus.length > 0 && complianceStatus[0] ? [
    { label: 'Compliant', value: complianceStatus[0]['Compliance'] || 0, color: '#10b981' },
    { label: 'Partial', value: complianceStatus[0]['Partial'] || 0, color: 'hsl(159, 100%, 51%)' },
    { label: 'Non-Compliant', value: complianceStatus[0]['NotCompliant'] || 0, color: '#ef4444' },
    { label: 'Not Started', value: complianceStatus[0]['Null'] || 0, color: '#7b9f96' }
  ] : [
    { label: 'Compliant', value: 0, color: '#10b981' },
    { label: 'Partial', value: 0, color: 'hsl(159, 100%, 51%)' },
    { label: 'Non-Compliant', value: 0, color: '#ef4444' },
    { label: 'Not Started', value: 0, color: '#7b9f96' }
  ];

  return (
    <div className="assessment-score-page">
      

      <div className="assessment-content">
        {/* Overview Section */}
        <section className="assessment-section">
          <h2 className="section-title">Overview</h2>
          <div className="assessment-grid">
            {/* <div className="score-card main-score">
              <h3>Overall Assessment Score</h3>
              <DonutChart percentage={overallScore} size={180} />
              <div className="score-details">
                <div className="score-level">
                  <span className="level-badge">Good</span>
                  <p>Your organization shows strong compliance readiness</p>
                </div>
              </div>
            </div> */}

            <div className="score-card">
              <h3>Risk Assessment Matrix</h3>
              <RadarChart data={standardMappingData} size={200} />
            </div>
            
            <div style={{ display: 'flex', gap: '1.5rem', width: '100%', gridColumn: '1 / -1' }}>
              <div 
                className="score-card" 
                style={{ 
                  width: '50%',
                  flex: '1'
                }}
              >
                <h3>Progress Timeline</h3>
                <LineChart data={timelineData} height={250} />
              </div>

              <div 
                className="control-mapping" 
                style={{ 
                  width: '50%',
                  flex: '1'
                }}
              >
                <h3>Control Mapping by Framework</h3>
                <BarChart data={controlMappingData} height={300} />
              </div>
            </div>
            
            <div 
              className="score-card" 
              style={{ 
                width: '100%',
                gridColumn: '1 / -1'
              }}
            >
              <h3>Objective Performance</h3>
              <BarChart data={objectiveScores} height={250} />
            </div>

            {/* Standards/Compliance Chart */}
            <div className="score-card">
              <h3>Standards/Compliance</h3>
              <BarChartComponent data={complianceForEachStandard} />
            </div>

            {/* Simple Tasks Chart */}
            <div className="score-card">
              <h3>Simple Tasks</h3>
              <LineChartComponent data={monthlyTasks} />
            </div>

            {/* Compliance Status Chart */}
            <div className="score-card">
              <h3>Compliance Status</h3>
              <PieChart data={complianceStatus} />
            </div>
          </div>
        </section>

        {/* Objectives Section */}
        <section className="assessment-section">
          <h2 className="section-title">Objectives</h2>
          <div className="objectives-section">
            <div className="objectives-grid">
              {objectiveScores.map((objective, index) => (
                <div key={index} className="objective-card">
                  <div className="objective-header">
                    <h4>{objective.label}</h4>
                    <DonutChart percentage={objective.value} size={100} color={objective.color} />
                  </div>
                  <div className="objective-details">
                    <div className="detail-item">
                      <span className="label">Status:</span>
                      <span className={`status ${objective.value >= 80 ? 'excellent' : objective.value >= 60 ? 'good' : 'needs-improvement'}`}>
                        {objective.value >= 80 ? 'Excellent' : objective.value >= 60 ? 'Good' : 'Needs Improvement'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Priority:</span>
                      <span className="priority">High</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Last Updated:</span>
                      <span className="date">2024-01-15</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Control Mapping Section */}
        <section className="assessment-section">
          <h2 className="section-title">Control Mapping</h2>
          <div className="controls-section">
            <div className="controls-overview">
              <div className="control-stats">
                <div className="stat-card">
                  <div className="stat-icon-wrapper" style={{ color: '#07ffa9' }}>
                    <Shield size={32} />
                  </div>
                  <h4>Total Controls</h4>
                  <span className="stat-number">247</span>
                </div>
                <div className="stat-card">
                  <div className="stat-icon-wrapper" style={{ color: '#07ffa9' }}>
                    <CheckCircle size={32} />
                  </div>
                  <h4>Implemented</h4>
                  <span className="stat-number">189</span>
                </div>
                <div className="stat-card">
                  <div className="stat-icon-wrapper" style={{ color: 'hsl(159, 100%, 51%)' }}>
                    <Clock size={32} />
                  </div>
                  <h4>In Progress</h4>
                  <span className="stat-number">34</span>
                </div>
                <div className="stat-card">
                  <div className="stat-icon-wrapper" style={{ color: '#ef4444' }}>
                    <Circle size={32} />
                  </div>
                  <h4>Not Started</h4>
                  <span className="stat-number">24</span>
                </div>
              </div>
            </div>
            
           
          </div>
        </section>

        {/* Standard Mapping Section */}
        <section className="assessment-section">
          <h2 className="section-title">Standard Mapping</h2>
          <div className="standards-section">
            
            
            <div className="standards-details">
              <h3>Standard Mapping Details</h3>
              <div className="standards-grid">
                {standardMappingData.map((standard, index) => (
                  <div key={index} className="standard-card">
                    <div className="standard-header">
                      <h4>{standard.label}</h4>
                      <span className="score">{standard.value}%</span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${standard.value}%` }}
                      ></div>
                    </div>
                    <div className="standard-metrics">
                      <div className="metric">
                        <span className="metric-label">Controls:</span>
                        <span className="metric-value">24</span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">Compliant:</span>
                        <span className="metric-value">18</span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">Gaps:</span>
                        <span className="metric-value">6</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ResultAssessment;